import { defineStore } from 'pinia'
import { fetchEmployeeMe } from '@/modules/User/requests/employeeMeApi'
import { formatEmployeeFullName } from '@/modules/User/utils/employeeProfileDisplay'
import {
  EmployeeStatus,
  type EmployeeProfileDto,
} from '@/modules/User/types/api/employee'

export type EmployeeProfileLoadStatus = 'idle' | 'loading' | 'success' | 'error'

interface State {
  profile: EmployeeProfileDto | null
  status: EmployeeProfileLoadStatus
}

let inflightLoad: Promise<EmployeeProfileDto | null> | null = null

const useUserStore = defineStore('user', {
  state: (): State => ({
    profile: null,
    status: 'idle',
  }),
  getters: {
    employeeMe(state): EmployeeProfileDto | null {
      return state.profile
    },
    fullName(state): string {
      return formatEmployeeFullName(state.profile)
    },
    positionTitle(state): string {
      return state.profile?.positions[0]?.title ?? '—'
    },
    isActive(state): boolean {
      return state.profile?.status === EmployeeStatus.Active
    },
    employeeId(state): number | null {
      return state.profile?.id ?? null
    },
    isProfileLoading(state): boolean {
      return state.status === 'loading'
    },
  },
  actions: {
    setProfile(profile: EmployeeProfileDto) {
      this.profile = profile
      this.status = 'success'
    },
    clearProfile() {
      this.profile = null
      this.status = 'idle'
      inflightLoad = null
    },
    async ensureProfile(): Promise<void> {
      await this.loadProfile({ force: false })
    },
    async refreshProfile(): Promise<void> {
      await this.loadProfile({ force: true })
    },
    async loadProfile(options?: {
      force?: boolean
    }): Promise<EmployeeProfileDto | null> {
      const force = options?.force ?? false

      if (!force && this.profile != null) {
        return this.profile
      }

      if (inflightLoad) {
        await inflightLoad
        if (!force && this.profile != null) {
          return this.profile
        }
      }

      const request = this.executeProfileFetch()
      inflightLoad = request

      try {
        return await request
      } finally {
        if (inflightLoad === request) {
          inflightLoad = null
        }
      }
    },
    async executeProfileFetch(): Promise<EmployeeProfileDto | null> {
      this.status = 'loading'

      try {
        const profile = await fetchEmployeeMe()
        this.profile = profile
        this.status = 'success'
        return profile
      } catch {
        this.status = 'error'
        return null
      }
    },
  },
})

export default useUserStore

export async function fetchEmployeeMeViaStore(): Promise<EmployeeProfileDto> {
  const store = useUserStore()
  await store.ensureProfile()

  if (store.profile == null) {
    throw new Error('Employee profile is unavailable')
  }

  return store.profile
}
